import type { ConnectionStatus } from '@lastrouter/contracts';
import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  Field,
  Input,
  NativeSelect,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@lastrouter/ui';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useApiMutation, useApiQuery } from '../api/hooks.js';
import { AdminOnly } from '../app/shell.js';
import { EmptyState, ErrorState, PageHeader, QueryState, StatusBadge } from '../components/common.js';
import { formatDateTime } from '../lib/format.js';

function TestDialog({ conn, onClose }: { conn: ConnectionStatus; onClose: () => void }) {
  const { t } = useTranslation();
  const catalog = useApiQuery('catalog');
  const test = useApiMutation('testConnection');
  const [user, setUser] = useState('');
  const [subject, setSubject] = useState('');
  const subjects = catalog.data?.snapshot?.subjects.filter((s) => s.fonte === conn.id) ?? [];

  function submit(e: FormEvent) {
    e.preventDefault();
    test.mutate({ params: { id: conn.id }, body: { user: user.trim(), ...(subject ? { subject_id: subject } : {}) } });
  }

  const r = test.data;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent title={t('connections.testTitle', { id: conn.id })} description={t('connections.testHelp')} closeLabel={t('common.close')}>
        <form className="flex flex-col gap-4" onSubmit={submit}>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field id="teste-usuario" label={t('connections.user')}>
              <Input value={user} onChange={(e) => setUser(e.target.value)} placeholder="CLUSTER\maria" required autoComplete="off" />
            </Field>
            <Field id="teste-assunto" label={t('connections.subject')}>
              <NativeSelect value={subject} onChange={(e) => setSubject(e.target.value)}>
                <option value="">{t('connections.allSubjects')}</option>
                {subjects.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.nome}
                  </option>
                ))}
              </NativeSelect>
            </Field>
          </div>
          <div>
            <Button type="submit" variant="primary" disabled={test.isPending || !user.trim()}>
              {t('connections.run')}
            </Button>
          </div>
        </form>
        {test.error ? <div className="mt-4"><ErrorState error={test.error} /></div> : null}
        {r ? (
          <div className="mt-4 flex flex-col gap-3" aria-live="polite">
            <Alert tone={r.ok ? 'success' : 'warning'} title={t('connections.summary', { user: r.user, ok: r.apps.filter((a) => a.ok).length, total: r.apps.length })} />
            <Table>
              <TableCaption>{t('connections.testTitle', { id: conn.id })}</TableCaption>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('connections.app')}</TableHead>
                  <TableHead>{t('connections.state')}</TableHead>
                  <TableHead>{t('connections.appTitle')}</TableHead>
                  <TableHead>{t('connections.lastReload')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {r.apps.map((a) => (
                  <TableRow key={`${a.subject_id}-${a.app}`}>
                    <TableCell>
                      <span className="font-mono text-[length:var(--lr-text-sm)]">{a.app}</span>
                      <p className="text-[length:var(--lr-text-xs)] text-fg-muted">{a.detail}</p>
                    </TableCell>
                    <TableCell>
                      <StatusBadge tone={a.ok ? 'success' : 'danger'}>{a.ok ? t('connections.ok') : t('connections.failed')}</StatusBadge>
                    </TableCell>
                    <TableCell>{a.title ?? '-'}</TableCell>
                    <TableCell className="tabular">{formatDateTime(a.last_reload_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

export function ConnectionsPage() {
  const { t } = useTranslation();
  const q = useApiQuery('connections');
  const [testing, setTesting] = useState<ConnectionStatus | null>(null);
  return (
    <AdminOnly>
      <PageHeader title={t('connections.title')} subtitle={t('connections.subtitle')} actions={<Button onClick={() => void q.refetch()}>{t('common.refresh')}</Button>} />
      <QueryState query={q}>
        {(list) =>
          list.length === 0 ? (
            <EmptyState>{t('connections.empty')}</EmptyState>
          ) : (
            <Table>
              <TableCaption>{t('connections.title')}</TableCaption>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('connections.id')}</TableHead>
                  <TableHead>{t('connections.type')}</TableHead>
                  <TableHead>{t('connections.target')}</TableHead>
                  <TableHead>{t('connections.state')}</TableHead>
                  <TableHead>{t('connections.checked')}</TableHead>
                  <TableHead>{t('connections.managedBy')}</TableHead>
                  <TableHead>
                    <span className="sr-only">{t('common.actions')}</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {list.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell className="font-medium">{c.id}</TableCell>
                    <TableCell>{c.type}</TableCell>
                    <TableCell className="font-mono text-[length:var(--lr-text-sm)]">{c.target}</TableCell>
                    <TableCell>
                      <StatusBadge tone={c.ok ? 'success' : 'danger'}>{c.ok ? t('connections.ok') : t('connections.failed')}</StatusBadge>
                      {!c.ok ? <p className="mt-1 text-[length:var(--lr-text-xs)] text-fg-secondary">{c.detail}</p> : null}
                    </TableCell>
                    <TableCell className="tabular">{formatDateTime(c.checked_at)}</TableCell>
                    <TableCell>{t(`connections.${c.managed_by}`)}</TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" onClick={() => setTesting(c)}>
                        {t('connections.test')}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )
        }
      </QueryState>
      {testing ? <TestDialog conn={testing} onClose={() => setTesting(null)} /> : null}
    </AdminOnly>
  );
}
